/**
 * Custom-report rate card — single source of truth for the auto-generated
 * "Role Market Report" product (Phase B).
 *
 * Pricing philosophy (affordable · competitive · unique · profitable): the
 * report is generated DETERMINISTICALLY from our own AI-enriched corpus — zero
 * marginal cost (no per-request AI, no analyst time), so margin is ~100% and we
 * can undercut agency salary/market reports (typically $100s–$1,000s) while
 * still being profitable at volume. Geo-priced like the advertiser rate card:
 * the 96%-majority Indonesian audience pays affordable IDR; the global remote
 * audience pays USD.
 *
 * A free, gated PREVIEW (headline stats only) is the top of funnel; the paid
 * tiers unlock the full report + CSV. Mayar self-serve checkout (Phase B2)
 * reads `idrOnce` for the IDR rail, so keep IDR amounts only here.
 *
 * `usdOnce` is DERIVED from `REPORT_USD_MINOR_BY_PLAN`
 * (`src/lib/billing/usd-catalog.ts`) — the same numbers the global Gumroad
 * rail actually charges (`REPORT_USD_MINOR` in `catalog-onetime-global.ts`).
 * Never hardcode a separate USD figure here; the rate card and the Gumroad
 * charge would drift (incident 2026-07-12: this page quoted $29/$79 while
 * Gumroad charged $39/$99).
 */

import { REPORT_USD_MINOR_BY_PLAN } from "@/lib/billing/usd-catalog";

export type ReportPlanKey = "single" | "bundle";

export interface ReportPlan {
	key: ReportPlanKey;
	/** One-time price in Indonesian rupiah (whole rupiah, not minor units). */
	idrOnce: number;
	/** One-time price in US dollars (whole dollars). */
	usdOnce: number;
	name: { en: string; id: string };
	blurb: { en: string; id: string };
	/** What the buyer gets, for the rate-card checklist. */
	includes: { en: string[]; id: string[] };
	/** True for the recommended/best-value tier (visually highlighted). */
	highlight?: boolean;
}

export const REPORT_PLANS: ReportPlan[] = [
	{
		key: "single",
		idrOnce: 449_000,
		usdOnce: REPORT_USD_MINOR_BY_PLAN.single / 100,
		name: {
			en: "Single Role Report",
			id: "Laporan Satu Peran",
		},
		blurb: {
			en: "One role family, your market — full report + raw CSV.",
			id: "Satu kelompok peran, pasar pilihanmu — laporan lengkap + CSV mentah.",
		},
		includes: {
			en: [
				"Skills in demand, ranked",
				"Seniority + remote-type mix",
				"Top hiring companies",
				"Salary band (where data is sufficient)",
				"Downloadable CSV",
			],
			id: [
				"Skill paling dicari, terurut",
				"Komposisi senioritas + tipe remote",
				"Perusahaan perekrut teratas",
				"Rentang gaji (bila data memadai)",
				"CSV bisa diunduh",
			],
		},
	},
	{
		key: "bundle",
		idrOnce: 1_290_000,
		usdOnce: REPORT_USD_MINOR_BY_PLAN.bundle / 100,
		name: {
			en: "Sector Bundle",
			id: "Bundle Sektor",
		},
		blurb: {
			en: "Five role-family reports + one 90-day refresh — best value.",
			id: "Lima laporan kelompok peran + satu pembaruan 90 hari — nilai terbaik.",
		},
		includes: {
			en: [
				"Any 5 role-family reports",
				"Everything in Single, per report",
				"One free refresh within 90 days",
				"Combined cross-role CSV",
			],
			id: [
				"5 laporan kelompok peran (bebas pilih)",
				"Semua isi paket Single, per laporan",
				"Satu pembaruan gratis dalam 90 hari",
				"CSV gabungan lintas-peran",
			],
		},
		highlight: true,
	},
];

export function getReportPlan(key: ReportPlanKey): ReportPlan | undefined {
	return REPORT_PLANS.find((p) => p.key === key);
}

export function formatReportIdr(amount: number): string {
	return `Rp ${amount.toLocaleString("id-ID")}`;
}

export function formatReportUsd(amount: number): string {
	return `$${amount.toLocaleString("en-US")}`;
}
